import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PortalAuthProvider } from './contexts/PortalAuthContext';
import { PortalLocationProvider } from './contexts/PortalLocationContext';
import { TutorialProvider } from './contexts/TutorialContext';
import { TutorialModal } from './components/Portal/Tutorial';
import { MillAuthProvider } from './contexts/MillAuthContext';
import { StoreAuthProvider } from './contexts/StoreAuthContext';
import { StoreClubAuthProvider } from './contexts/StoreClubAuthContext';
import { BridgeClubAuthProvider } from './contexts/BridgeClubAuthContext';
import { RefreshProvider } from './contexts/RefreshContext';
import { DraftProvider } from './contexts/DraftContext';
import ProtectedRoute from './components/ProtectedRoute';
import PortalProtectedRoute from './components/PortalProtectedRoute';
import MillProtectedRoute from './components/MillProtectedRoute';
import StoreProtectedRoute from './components/StoreProtectedRoute';
import StoreClubProtectedRoute from './components/StoreClubProtectedRoute';
import BridgeClubProtectedRoute from './components/BridgeClubProtectedRoute';
import DeliveryProtectedRoute from './components/DeliveryProtectedRoute';
import Header from './components/Layout/Header';
import PortalHeader from './components/Layout/PortalHeader';
import MillHeader from './components/Layout/MillHeader';
import DeliveryHeader from './components/Layout/DeliveryHeader';
import InstallPrompt from './components/InstallPrompt';
import Login from './pages/Login';
import StaffVerifyEmail from './pages/Auth/StaffVerifyEmail';
import Dashboard from './pages/Dashboard';
import OrderEntry from './pages/OrderEntry';
import Customers from './pages/Customers';
import PendingRegistrations from './pages/PendingRegistrations';
import TaxDocumentsReview from './pages/TaxDocumentsReview';
import StoreClubMembers from './pages/StoreClubMembers';
import UserManagement from './pages/UserManagement';
import Orders from './pages/Orders';
import FeedFormulas from './pages/FeedFormulas';
import Inventory from './pages/Inventory';
import PortalLogin from './pages/Portal/PortalLogin';
import PortalVerifyEmail from './pages/Portal/PortalVerifyEmail';
import PortalDashboard from './pages/Portal/PortalDashboard';
import PortalOrders from './pages/Portal/PortalOrders';
import PortalQuotes from './pages/Portal/PortalQuotes';
import PortalPlaceOrder from './pages/Portal/PortalPlaceOrder';
import TaxExemption from './pages/Portal/TaxExemption';
import EmailPreferences from './pages/Portal/EmailPreferences';
import PortalSettings from './pages/Portal/PortalSettings';
import PortalAPIIntegration from './pages/Portal/PortalAPIIntegration';
import PortalOrderHistory from './pages/Portal/PortalOrderHistory';
import PortalAnnualReport from './pages/Portal/PortalAnnualReport';
import PortalReorderSuggestions from './pages/Portal/PortalReorderSuggestions';
import ReportsHub from './pages/Portal/ReportsHub';
import ReportsBatches from './pages/Portal/ReportsBatches';
import BatchReportView from './pages/Portal/BatchReportView';
import PortalBilling from './pages/Portal/PortalBilling';
import PortalBridgeClub from './pages/Portal/PortalBridgeClub';
import PortalMemberDetail from './pages/Portal/PortalMemberDetail';
import PortalTeam from './pages/Portal/PortalTeam';
import PortalFarmTeam from './pages/Portal/PortalFarmTeam';
import FarmTasks from './pages/Portal/FarmTasks';
import FarmCrewDashboard from './pages/Portal/FarmCrewDashboard';
import PortalResources from './pages/Portal/PortalResources';
import PortalMyDocuments from './pages/Portal/PortalMyDocuments';
import PortalPredatorIdentifier from './pages/Portal/PortalPredatorIdentifier';
import PortalSetPassword from './pages/Portal/PortalSetPassword';
import PortalChangePassword from './pages/Portal/PortalChangePassword';
import Production from './pages/Production';
import ProductionBatchDetail from './pages/ProductionBatchDetail';
import BatchMixing from './pages/BatchMixing';
import BatchSearch from './pages/BatchSearch';
import BillsOfLading from './pages/BillsOfLading';
import AdminReports from './pages/AdminReports';
import MilkRuns from './pages/MilkRuns';
import MillLogin from './pages/Mill/MillLogin';
import MillDashboard from './pages/Mill/MillDashboard';
import MillMixing from './pages/Mill/MillMixing';
import MillPackaging from './pages/Mill/MillPackaging';
import MillShipping from './pages/Mill/MillShipping';
import MillInventory from './pages/Mill/MillInventory';
import MillFeedBank from './pages/Mill/MillFeedBank';
import MillCalendar from './pages/Mill/MillCalendar';
import MillOrders from './pages/Mill/MillOrders';
import MillCommandDashboard from './pages/Mill/MillCommandDashboard';
import RecallLookup from './pages/Mill/RecallLookup';
import LabelManagement from './pages/Mill/LabelManagement';
import CRMDashboard from './pages/CRM/CRMDashboard';
import LeadsList from './pages/CRM/LeadsList';
import LeadDetail from './pages/CRM/LeadDetail';
import LeadForm from './pages/CRM/LeadForm';
import Pipeline from './pages/CRM/Pipeline';
import CRMTasks from './pages/CRM/CRMTasks';
import CustomerCRM from './pages/CRM/CustomerCRM';
import CallLog from './pages/CRM/CallLog';
import MondayTaskList from './pages/CRM/MondayTaskList';
import NoContactList from './pages/CRM/NoContactList';
import CRMCalendar from './pages/CRM/CRMCalendar';
import BookingSettings from './pages/CRM/BookingSettings';
import PromoCampaigns from './pages/CRM/PromoCampaigns';
import PromoCampaignDetail from './pages/CRM/PromoCampaignDetail';
import PasskeySettings from './pages/Settings/PasskeySettings';
import QuickBooksSettings from './pages/Settings/QuickBooksSettings';
import QuickBooksItemMapping from './pages/Settings/QuickBooksItemMapping';
import NotificationSettings from './pages/Settings/NotificationSettings';
import PriceManagement from './pages/Admin/PriceManagement';
import EmailPortal from './pages/Admin/EmailPortal';
import BridgeClubAdmin from './pages/Admin/BridgeClubAdmin';
import AnnouncementsAdmin from './pages/Admin/AnnouncementsAdmin';
import PartnerPromotionsAdmin from './pages/Admin/PartnerPromotionsAdmin';
import AdminBridgeClubPurchases from './pages/Admin/BridgeClubPurchases';
import AdminBridgeClubRewards from './pages/Admin/BridgeClubRewards';
import AdminBridgeClubCredits from './pages/Admin/BridgeClubCredits';
import AdminBridgeClubAnalytics from './pages/Admin/BridgeClubAnalytics';
import AdminBridgeClubPromotions from './pages/Admin/BridgeClubPromotions';
import BridgeClubNetworkOverview from './pages/Admin/BridgeClubNetworkOverview';
import BridgeClubStoreDetail from './pages/Admin/BridgeClubStoreDetail';
import BridgeClubOpportunities from './pages/Admin/BridgeClubOpportunities';
import BridgeClubMemberDetail from './pages/Admin/BridgeClubMemberDetail';
import UnmatchedVisitorDetail from './pages/Admin/UnmatchedVisitorDetail';
import BridgeClubPOSReceiptDetail from './pages/Admin/BridgeClubPOSReceiptDetail';
import WillCallAnalytics from './pages/Admin/WillCallAnalytics';
import WalkInAnalytics from './pages/Admin/WalkInAnalytics';
import PipelineHealth from './pages/Admin/PipelineHealth';
import InTransit from './pages/Admin/InTransit';
import StoreLedger from './pages/Admin/StoreLedger';
import BridgeClubQRAnalytics from './pages/Admin/BridgeClubQRAnalytics';
import SecurityDashboard from './pages/Admin/SecurityDashboard';
import EufySettings from './pages/Admin/EufySettings';
import AdminPartnerAPI from './pages/Admin/AdminPartnerAPI';
import LauraInsights from './pages/Admin/LauraInsights';
import LauraLearning from './pages/Admin/LauraLearning';
import PrayerRequests from './pages/Admin/PrayerRequests';
import OrderDetail from './pages/Admin/OrderDetail';
import DiscountCategories from './pages/Admin/DiscountCategories';
import AdminResources from './pages/AdminResources';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import StoreLogin from './pages/Store/StoreLogin';
import StoreDashboard from './pages/Store/StoreDashboard';
import StoreInventory from './pages/Store/StoreInventoryList';
import StoreOrderEntry from './pages/Store/StoreOrderEntry';
import StoreBulkBins from './pages/Store/StoreBulkBins';
import StoreScan from './pages/Store/StoreScan';
import StoreClubOrders from './pages/Store/StoreClubOrders';
import StoreCheckin from './pages/Store/StoreCheckin';
import StoreBridgeClub from './pages/Store/StoreBridgeClub';
import StoreTasks from './pages/Store/StoreTasks';
import StoreAPIIntegration from './pages/Store/StoreAPIIntegration';
import StorePOS from './pages/Store/StorePOS';
import POSDailyReport from './pages/Store/POSDailyReport';
import ScanGoVerification from './pages/Store/ScanGoVerification';
import StoreAdmin from './pages/Store/StoreAdmin';
import POSProductsAdmin from './pages/Store/POSProductsAdmin';
import BarcodeScanner from './pages/Store/BarcodeScanner';
import BYOCLabels from './pages/Store/BYOCLabels';
import ProductQRCodes from './pages/Store/ProductQRCodes';
import LockerDashboard from './pages/Store/LockerDashboard';
import PickupOrders from './pages/Store/PickupOrders';
import OrdersHistory from './pages/Store/OrdersHistory';
import StoreOrders from './pages/Store/StoreOrders';
import BridgeClubOrderDetail from './pages/Store/BridgeClubOrderDetail';
import NewOrderWatcher from './components/Store/NewOrderWatcher';
import { OnboardingFlow, ClubLogin, ClubDashboard, ClubShop, ClubCart, ClubCheckout, ClubOrders, ClubOrderDetail, ClubFarm, ClubProfile, ClubCard, LocalCheck } from './pages/StoreClub';
// Bridge Club imports
import BridgeClubLogin from './pages/BridgeClub/BridgeClubLogin';
import BridgeClubResetPassword from './pages/BridgeClub/BridgeClubResetPassword';
import BridgeClubVerifyEmail from './pages/BridgeClub/BridgeClubVerifyEmail';
import BridgeClubCardLink from './pages/BridgeClub/CardLinkPage';
import BridgeClubDashboard from './pages/BridgeClub/BridgeClubDashboard';
import BridgeClubCard from './pages/BridgeClub/BridgeClubCard';
import DeliveryPage from './components/BridgeClub/DeliveryPage';
import BridgeClubAnimals from './pages/BridgeClub/BridgeClubAnimals';
import BridgeClubFeed from './pages/BridgeClub/BridgeClubFeed';
import BridgeClubPurchases from './pages/BridgeClub/BridgeClubPurchases';
import BridgeClubUploadReceipt from './pages/BridgeClub/BridgeClubUploadReceipt';
import BridgeClubProfile from './pages/BridgeClub/BridgeClubProfile';
import BridgeClubStoreFinder from './pages/BridgeClub/BridgeClubStoreFinder';
import BridgeClubResources from './pages/BridgeClub/BridgeClubResources';
import PredatorIdentifierPage from './pages/BridgeClub/PredatorIdentifierPage';
import FlockHealthAssistantPage from './pages/BridgeClub/FlockHealthAssistantPage';
import BridgeClubOnboarding from './pages/BridgeClub/Onboarding/OnboardingFlow';
import BridgeClubSetupFlow from './pages/BridgeClub/Onboarding/SetupFlow';
import BridgeClubTerms from './pages/BridgeClub/BridgeClubTerms';
import BridgeClubPrivacy from './pages/BridgeClub/BridgeClubPrivacy';
import FarmChallengeHub from './pages/BridgeClub/FarmChallenge/FarmChallengeHub';
import FarmChallengeOptIn from './pages/BridgeClub/FarmChallenge/FarmChallengeOptIn';
import FarmChallengeJoin from './pages/BridgeClub/FarmChallenge/FarmChallengeJoin';
import FarmChallengeHistory from './pages/BridgeClub/FarmChallenge/FarmChallengeHistory';
import FarmChallengeRedeem from './pages/BridgeClub/FarmChallenge/FarmChallengeRedeem';
// Local Access Shopping (Mill Members)
import LocalShop from './pages/BridgeClub/LocalShop';
import LocalCart from './pages/BridgeClub/LocalCart';
import LocalOrders from './pages/BridgeClub/LocalOrders';
import LocalOrderDetail from './pages/BridgeClub/LocalOrderDetail';
import LocalBYOC from './pages/BridgeClub/LocalBYOC';
import BulkPreOrder from './pages/BridgeClub/BulkPreOrder';
import BulkOrderDetail from './pages/BridgeClub/BulkOrderDetail';
import BCTaxExemption from './pages/BridgeClub/TaxExemption';
// Scan & Go Self-Checkout
import ScanAndGo from './pages/BridgeClub/ScanAndGo';
import ScanAndGoCheckout from './pages/BridgeClub/ScanAndGoCheckout';
import ScanAndGoReceipt from './pages/BridgeClub/ScanAndGoReceipt';
import MillStore from './pages/BridgeClub/MillStore';
// Store Locator (public)
import StoreLocator from './pages/StoreLocator/StoreLocator';
import StoreLocatorEmbed from './pages/StoreLocator/StoreLocatorEmbed';
import DeliveryHome from './pages/Delivery/DeliveryHome';
import DriverRoot from './pages/Delivery/driver/DriverRoot';
// Eager, like DriverRoot: the property walk must open on a property with no signal.
import WalkRoot from './pages/Delivery/walk/WalkRoot';
import BookingPage from './pages/Booking/BookingPage';
import BookingNDAGate from './pages/Booking/BookingNDAGate';
import AgreementsAdmin from './pages/Admin/AgreementsAdmin';
import SupportTicketsAdmin from './pages/Admin/SupportTicketsAdmin';
import BulkVisibilityAdmin from './pages/Admin/BulkVisibilityAdmin';
import PortalSupportTickets from './pages/Portal/PortalSupportTickets';
import PortalNewTicket from './pages/Portal/PortalNewTicket';
import PortalTicketDetail from './pages/Portal/PortalTicketDetail';
import PortalErrorBoundary from './components/Portal/ErrorBoundary';
import { PlannerAdmin, SeasonalPlansAdmin, PlannedOrdersAdmin, BatchesAdmin, FeedProfilesAdmin, WeatherTipsAdmin, CreatePlanAdmin, EggWatchAdminDashboard, EggWatchFarmsList, EggWatchFarmDetail, EggWatchFeedPerformance, EggWatchAdminAlerts, EggWatchExport, FeedAlertsAdmin } from './pages/Planner';
import { PlannerDashboard, PlannerBatches, PlannerWeather, OrderVerification, CreatePlanWizard, EditPlan, ViewPlan, BatchDetailPage, EggWatchHub, FarmHealthDashboard, FarmhandGetStarted, PastSeasons } from './pages/Portal/Planner';
import { FarmMapPage } from './components/Portal/FarmMap';
// Farmhand is paused while we rebuild as Farmer's Partner (Feb 2027). The
// coming-soon page replaces the live experience; the sub-routes/components
// above are intentionally kept imported (not deleted) for the rebuild and now
// redirect to the landing page.
import PortalFarmhandComingSoon from './pages/Portal/PortalFarmhandComingSoon';
import { KnowledgeDashboard, PendingQuestions, ArticlesList, NewArticle, DocumentsList } from './pages/Knowledge';
import { LauraVoiceAssistant } from './components/Voice';
import './styles/globals.css';
import './App.css';

// Delivery Portal is lazy-loaded — its entry point is the only code-split page
// here (all other imports above stay static; no router refactor in this build).
const DeliveryDashboard = React.lazy(() => import('./pages/Delivery/DeliveryDashboard'));
const DeliveryLeads = React.lazy(() => import('./pages/Delivery/DeliveryLeads'));
const DeliveryLeadsMap = React.lazy(() => import('./pages/Delivery/DeliveryLeadsMap'));
const DeliveryLeadDetail = React.lazy(() => import('./pages/Delivery/DeliveryLeadDetail'));
const PetMapping = React.lazy(() => import('./pages/Delivery/PetMapping'));
const DeliveryCatalog = React.lazy(() => import('./pages/Delivery/DeliveryCatalog'));
const DeliveryRoutes = React.lazy(() => import('./pages/Delivery/DeliveryRoutes'));
const DeliveryActivation = React.lazy(() => import('./pages/Delivery/DeliveryActivation'));
const DeliveryLanding = React.lazy(() => import('./pages/Delivery/DeliveryLanding'));
const DeliveryOrders = React.lazy(() => import('./pages/Delivery/DeliveryOrders'));
const DeliveryProcurement = React.lazy(() => import('./pages/Delivery/DeliveryProcurement'));
// The driver view is imported EAGERLY: an unfetched lazy chunk would leave an
// offline start on "Loading…". See pages/Delivery/driver/DriverRoot.tsx.
// Vendor Portal — standalone scoped external vendor page (own login, one category).
const VendorPortal = React.lazy(() => import('./pages/Vendor/VendorPortal'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Disable browser's automatic scroll restoration (fights our manual scroll)
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    // Defer scroll to after React renders the new route's content
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, [pathname]);
  return null;
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/login';
  const isPortalRoute = location.pathname.startsWith('/portal');
  const isPortalLoginPage = location.pathname === '/portal/login';
  const isPortalSetPasswordPage = location.pathname === '/portal/set-password';
  const isMillRoute = location.pathname.startsWith('/mill');
  const isMillLoginPage = location.pathname === '/mill/login';
  const isMillCommandPage = location.pathname === '/mill/command';
  const isStoreRoute = location.pathname.startsWith('/store');
  const isStoreLoginPage = location.pathname === '/store/login';
  const isClubRoute = location.pathname.startsWith('/club') || location.pathname.startsWith('/store-club');
  const isBridgeClubRoute = location.pathname.startsWith('/bridge-club');
  const isPublicStoreLocator = location.pathname.startsWith('/find-a-store');
  const isDeliveryRoute = location.pathname === '/delivery' || location.pathname.startsWith('/delivery/');
  // The driver view is full-screen: one action per screen, no portal nav.
  const isDriverRoute = location.pathname === '/delivery/driver' || location.pathname.startsWith('/delivery/driver/');

  // NOTE: the admin Header no longer takes a `user` prop — it reads authUser from
  // AuthContext (cookie-backed /api/auth/me/). The old localStorage user keys
  // ('rbf_user_data' / 'user') were never written post cookie-migration, so gating
  // the nav on them silently hid the whole navigation for anyone whose stale value
  // had been cleared. Source of truth is the cookie session, not localStorage.

  // Subdomain auto-redirect
  // Each portal subdomain redirects to its specific route on load
  // IMPORTANT: Preserve query params (store, placement) for QR tracking
  useEffect(() => {
    const config = (window as any).__SUBDOMAIN_CONFIG__ || {};
    const path = location.pathname;
    const search = location.search; // Preserve ?store=...&placement=... params

    // Wholesale subdomain → /portal (customer portal)
    if (config.isWholesale || window.location.hostname.startsWith('wholesale.')) {
      if (!path.startsWith('/portal') && path !== '/portal/login' && path !== '/portal/set-password') {
        navigate(`/portal${search}`, { replace: true });
      }
    }
    // Bridge Club subdomain → /bridge-club
    else if (config.isBridgeClub || window.location.hostname.startsWith('bridgeclub.')) {
      if (!path.startsWith('/bridge-club')) {
        navigate(`/bridge-club${search}`, { replace: true });
      }
    }
    // Store Club subdomain → /club
    else if (config.isStoreClub || window.location.hostname.startsWith('storeclub.')) {
      if (!path.startsWith('/club') && !path.startsWith('/store-club')) {
        navigate(`/club${search}`, { replace: true });
      }
    }
  }, [location.pathname, location.search, navigate]);

  // Determine install prompt config based on subdomain
  const config = (window as any).__SUBDOMAIN_CONFIG__ || {};
  let installAppName = '';
  let installThemeColor = '';

  if (config.isWholesale || window.location.hostname.startsWith('wholesale.')) {
    installAppName = 'Red Bridge Wholesale';
    installThemeColor = '#8B2131';
  } else if (config.isBridgeClub || window.location.hostname.startsWith('bridgeclub.')) {
    installAppName = 'Bridge Club';
    installThemeColor = '#4A6741';
  } else if (config.isStoreClub || window.location.hostname.startsWith('storeclub.')) {
    installAppName = 'Store Club';
    installThemeColor = '#B8860B';
  }

  return (
    <>
      {!isLoginPage && !isPortalRoute && !isMillRoute && !isStoreRoute && !isClubRoute && !isBridgeClubRoute && !isPublicStoreLocator && !isDeliveryRoute && <Header />}
      {isPortalRoute && !isPortalLoginPage && !isPortalSetPasswordPage && <PortalHeader />}
      {/* Tutorial Modal for Portal - renders overlay when tutorial is active */}
      {isPortalRoute && !isPortalLoginPage && !isPortalSetPasswordPage && <TutorialModal />}
      {isMillRoute && !isMillLoginPage && !isMillCommandPage && <MillHeader />}
      {isDeliveryRoute && !isDriverRoute && <DeliveryHeader />}
      {/* Laura Voice Assistant - hidden for now, re-enable when ready
      {user && !isLoginPage && !isPortalRoute && !isMillRoute && !isStoreRoute && !isClubRoute && !isBridgeClubRoute && !isPublicStoreLocator && (
        <LauraVoiceAssistant portal="admin" />
      )}
      */}
      <Routes>
        {/* Admin Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/verify-email/:token" element={<StaffVerifyEmail />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/order-entry"
          element={
            <ProtectedRoute>
              <OrderEntry />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <ProtectedRoute>
              <Customers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers/pending-registrations"
          element={
            <ProtectedRoute>
              <PendingRegistrations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers/tax-documents-review"
          element={
            <ProtectedRoute>
              <TaxDocumentsReview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers/store-club"
          element={
            <ProtectedRoute>
              <StoreClubMembers />
            </ProtectedRoute>
          }
        />
        {/* Deep link to a single customer; reuses Customers.tsx, which opens the
            modal for the URL-supplied id. Static segments above (pending-registrations,
            tax-documents-review, store-club) win on specificity in React Router v6. */}
        <Route
          path="/customers/:id"
          element={
            <ProtectedRoute>
              <Customers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <ProtectedRoute>
              <OrderDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/feed-formulas"
          element={
            <ProtectedRoute>
              <FeedFormulas />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory"
          element={
            <ProtectedRoute>
              <Inventory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/production"
          element={
            <ProtectedRoute>
              <Production />
            </ProtectedRoute>
          }
        />
        <Route
          path="/production/batch/:id"
          element={
            <ProtectedRoute>
              <ProductionBatchDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/production/mixing/:id"
          element={
            <ProtectedRoute>
              <BatchMixing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/batches"
          element={
            <ProtectedRoute>
              <BatchSearch />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bills-of-lading"
          element={
            <ProtectedRoute>
              <BillsOfLading />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <AdminReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/milk-runs"
          element={
            <ProtectedRoute>
              <MilkRuns />
            </ProtectedRoute>
          }
        />

        {/* Settings Routes */}
        <Route
          path="/settings/passkeys"
          element={
            <ProtectedRoute>
              <PasskeySettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/quickbooks"
          element={
            <ProtectedRoute>
              <QuickBooksSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/quickbooks/item-mapping"
          element={
            <ProtectedRoute>
              <QuickBooksItemMapping />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/notifications"
          element={
            <ProtectedRoute>
              <NotificationSettings />
            </ProtectedRoute>
          }
        />
        <Route path="/invoice-review" element={<Navigate to="/sales/invoices" replace />} />
        <Route path="/invoices" element={<Navigate to="/sales/invoices" replace />} />
        {/* New Invoices pages */}
        <Route
          path="/sales/invoices"
          element={
            <ProtectedRoute>
              <Invoices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales/invoices/:id"
          element={
            <ProtectedRoute>
              <InvoiceDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/pricing"
          element={
            <ProtectedRoute>
              <PriceManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/emails"
          element={
            <ProtectedRoute>
              <EmailPortal />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club"
          element={
            <ProtectedRoute>
              <BridgeClubAdmin />
            </ProtectedRoute>
          }
        />
        {/* POS Back Office - Product/Price Management */}
        <Route
          path="/admin/pos/products"
          element={
            <ProtectedRoute>
              <POSProductsAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/purchases"
          element={
            <ProtectedRoute>
              <AdminBridgeClubPurchases />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/rewards"
          element={
            <ProtectedRoute>
              <AdminBridgeClubRewards />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/credits"
          element={
            <ProtectedRoute>
              <AdminBridgeClubCredits />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/analytics"
          element={
            <ProtectedRoute>
              <AdminBridgeClubAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/promotions"
          element={
            <ProtectedRoute>
              <AdminBridgeClubPromotions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/network"
          element={
            <ProtectedRoute>
              <BridgeClubNetworkOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/stores/:storeId"
          element={
            <ProtectedRoute>
              <BridgeClubStoreDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/opportunities"
          element={
            <ProtectedRoute>
              <BridgeClubOpportunities />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/members/:memberId"
          element={
            <ProtectedRoute>
              <BridgeClubMemberDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/walk-ins/:visitorId"
          element={
            <ProtectedRoute>
              <UnmatchedVisitorDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/pos-receipt/:transactionNumber"
          element={
            <ProtectedRoute>
              <BridgeClubPOSReceiptDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/will-call/analytics"
          element={
            <ProtectedRoute>
              <WillCallAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/walk-ins/analytics"
          element={
            <ProtectedRoute>
              <WalkInAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/pipeline-health"
          element={
            <ProtectedRoute>
              <PipelineHealth />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/in-transit"
          element={
            <ProtectedRoute>
              <InTransit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/store-ledger"
          element={
            <ProtectedRoute>
              <StoreLedger />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/bridge-club/qr-analytics"
          element={
            <ProtectedRoute>
              <BridgeClubQRAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/security"
          element={
            <ProtectedRoute>
              <SecurityDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/agreements"
          element={
            <ProtectedRoute>
              <AgreementsAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/support-tickets"
          element={
            <ProtectedRoute>
              <SupportTicketsAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/announcements"
          element={
            <ProtectedRoute>
              <AnnouncementsAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/partner-promotions"
          element={
            <ProtectedRoute>
              <PartnerPromotionsAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/feed-formulas/bulk-visibility"
          element={
            <ProtectedRoute>
              <BulkVisibilityAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/eufy-settings"
          element={
            <ProtectedRoute>
              <EufySettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/partner-api"
          element={
            <ProtectedRoute>
              <AdminPartnerAPI />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/laura-insights"
          element={
            <ProtectedRoute>
              <LauraInsights />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/laura-learning"
          element={
            <ProtectedRoute>
              <LauraLearning />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/prayer-requests"
          element={
            <ProtectedRoute>
              <PrayerRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/resources"
          element={
            <ProtectedRoute>
              <AdminResources />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/discount-categories"
          element={
            <ProtectedRoute>
              <DiscountCategories />
            </ProtectedRoute>
          }
        />
        {/* CRM Routes */}
        <Route
          path="/crm"
          element={
            <ProtectedRoute>
              <CRMDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/leads"
          element={
            <ProtectedRoute>
              <LeadsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/leads/new"
          element={
            <ProtectedRoute>
              <LeadForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/leads/:id"
          element={
            <ProtectedRoute>
              <LeadDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/leads/:id/edit"
          element={
            <ProtectedRoute>
              <LeadForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/pipeline"
          element={
            <ProtectedRoute>
              <Pipeline />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/tasks"
          element={
            <ProtectedRoute>
              <CRMTasks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/customers"
          element={
            <ProtectedRoute>
              <CustomerCRM />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/calls"
          element={
            <ProtectedRoute>
              <CallLog />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/customers/:id"
          element={
            <ProtectedRoute>
              <CustomerCRM />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/monday"
          element={
            <ProtectedRoute>
              <MondayTaskList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/no-contact"
          element={
            <ProtectedRoute>
              <NoContactList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/calendar"
          element={
            <ProtectedRoute>
              <CRMCalendar />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/booking-settings"
          element={
            <ProtectedRoute>
              <BookingSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/crm/promo-campaigns"
          element={
            <ProtectedRoute>
              <PromoCampaigns />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm/promo-campaigns/:id"
          element={
            <ProtectedRoute>
              <PromoCampaignDetail />
            </ProtectedRoute>
          }
        />

        {/* Planner Admin Routes */}
        <Route
          path="/planner"
          element={
            <ProtectedRoute>
              <PlannerAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planner/plans"
          element={
            <ProtectedRoute>
              <SeasonalPlansAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planner/orders"
          element={
            <ProtectedRoute>
              <PlannedOrdersAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planner/batches"
          element={
            <ProtectedRoute>
              <BatchesAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planner/profiles"
          element={
            <ProtectedRoute>
              <FeedProfilesAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planner/weather-tips"
          element={
            <ProtectedRoute>
              <WeatherTipsAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planner/plans/new"
          element={
            <ProtectedRoute>
              <CreatePlanAdmin />
            </ProtectedRoute>
          }
        />

        {/* Egg Watch Admin Routes */}
        <Route
          path="/planner/egg-watch"
          element={
            <ProtectedRoute>
              <EggWatchAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planner/egg-watch/farms"
          element={
            <ProtectedRoute>
              <EggWatchFarmsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planner/egg-watch/farms/:farmId"
          element={
            <ProtectedRoute>
              <EggWatchFarmDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planner/egg-watch/feed-performance"
          element={
            <ProtectedRoute>
              <EggWatchFeedPerformance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planner/egg-watch/alerts"
          element={
            <ProtectedRoute>
              <EggWatchAdminAlerts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/planner/egg-watch/export"
          element={
            <ProtectedRoute>
              <EggWatchExport />
            </ProtectedRoute>
          }
        />

        {/* Feed Alerts Admin */}
        <Route
          path="/planner/feed-alerts"
          element={
            <ProtectedRoute>
              <FeedAlertsAdmin />
            </ProtectedRoute>
          }
        />

        {/* Knowledge Base Routes */}
        <Route
          path="/knowledge"
          element={
            <ProtectedRoute>
              <KnowledgeDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge/pending"
          element={
            <ProtectedRoute>
              <PendingQuestions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge/articles"
          element={
            <ProtectedRoute>
              <ArticlesList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge/articles/new"
          element={
            <ProtectedRoute>
              <NewArticle />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge/documents"
          element={
            <ProtectedRoute>
              <DocumentsList />
            </ProtectedRoute>
          }
        />

        {/* Portal Routes */}
        <Route path="/portal" element={<Navigate to="/portal/dashboard" replace />} />
        <Route path="/portal/login" element={<PortalLogin />} />
        <Route path="/portal/verify-email/:token" element={<PortalVerifyEmail />} />
        {/* Set password route - PUBLIC (for team member invitations) */}
        <Route path="/portal/set-password" element={<PortalSetPassword />} />
        <Route
          path="/portal/dashboard"
          element={
            <PortalProtectedRoute>
              <PortalDashboard />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/orders"
          element={
            <PortalProtectedRoute>
              <PortalOrders />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/quotes"
          element={
            <PortalProtectedRoute>
              <PortalQuotes />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/place-order"
          element={
            <PortalProtectedRoute>
              <PortalPlaceOrder />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/tax-exemption"
          element={
            <PortalProtectedRoute>
              <TaxExemption />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/email-preferences"
          element={
            <PortalProtectedRoute>
              <EmailPreferences />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/settings"
          element={
            <PortalProtectedRoute>
              <PortalSettings />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/change-password"
          element={
            <PortalProtectedRoute>
              <PortalChangePassword />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/api-integration"
          element={
            <PortalProtectedRoute>
              <PortalAPIIntegration />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/reports"
          element={
            <PortalProtectedRoute>
              <ReportsHub />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/reports/order-history"
          element={
            <PortalProtectedRoute>
              <PortalOrderHistory />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/reports/annual"
          element={
            <PortalProtectedRoute>
              <PortalAnnualReport />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/reports/batches"
          element={
            <PortalProtectedRoute>
              <ReportsBatches />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/reports/batches/:batchId"
          element={
            <PortalProtectedRoute>
              <BatchReportView />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/reports/reorder-suggestions"
          element={
            <PortalProtectedRoute>
              <PortalReorderSuggestions />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/billing"
          element={
            <PortalProtectedRoute>
              <PortalBilling />
            </PortalProtectedRoute>
          }
        />

        {/* Portal Bridge Club Routes */}
        <Route
          path="/portal/bridge-club"
          element={
            <PortalProtectedRoute>
              <PortalBridgeClub />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/bridge-club/members/:memberId"
          element={
            <PortalProtectedRoute>
              <PortalMemberDetail />
            </PortalProtectedRoute>
          }
        />

        {/* Portal Team Management Route */}
        <Route
          path="/portal/team"
          element={
            <PortalProtectedRoute>
              <PortalTeam />
            </PortalProtectedRoute>
          }
        />

        {/* Farm Team Management Route (Wholesale) */}
        <Route
          path="/portal/farm-team"
          element={
            <PortalProtectedRoute>
              <PortalFarmTeam />
            </PortalProtectedRoute>
          }
        />

        {/* Farm Tasks Route (Wholesale) */}
        <Route
          path="/portal/farm-tasks"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />

        {/* Farm Crew Dashboard (Simplified view for crew members) */}
        <Route
          path="/portal/crew-dashboard"
          element={
            <PortalProtectedRoute>
              <FarmCrewDashboard />
            </PortalProtectedRoute>
          }
        />

        {/* Portal Resources Routes */}
        <Route
          path="/portal/resources"
          element={
            <PortalProtectedRoute>
              <PortalResources />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/resources/:slug"
          element={
            <PortalProtectedRoute>
              <PortalResources />
            </PortalProtectedRoute>
          }
        />

        {/* Portal My Documents (Account-Specific) */}
        <Route
          path="/portal/my-documents"
          element={
            <PortalProtectedRoute>
              <PortalMyDocuments />
            </PortalProtectedRoute>
          }
        />

        {/* Redirect old /portal/help to merged support page */}
        <Route path="/portal/help" element={<Navigate to="/portal/support?tab=guides" replace />} />

        {/* Portal Help & Support (merged guides + tickets) */}
        <Route
          path="/portal/support"
          element={
            <PortalProtectedRoute>
              <PortalErrorBoundary>
                <PortalSupportTickets />
              </PortalErrorBoundary>
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/support/new"
          element={
            <PortalProtectedRoute>
              <PortalErrorBoundary>
                <PortalNewTicket />
              </PortalErrorBoundary>
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/support/:id"
          element={
            <PortalProtectedRoute>
              <PortalErrorBoundary>
                <PortalTicketDetail />
              </PortalErrorBoundary>
            </PortalProtectedRoute>
          }
        />

        {/* Portal Tools Routes */}
        <Route
          path="/portal/tools/predator-id"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />

        {/* Portal Farmhand Routes */}
        <Route
          path="/portal/farmhand/get-started"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/farmhand/past-seasons"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/farmhand"
          element={
            <PortalProtectedRoute>
              <PortalFarmhandComingSoon />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/farmhand/batches"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/farmhand/batches/:batchId"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/farmhand/weather"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/farmhand/create"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/farmhand/plans/:planId/edit"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/farmhand/plans/:planId"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/farmhand/plans/:planId/batch/:batchId"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/farmhand/egg-watch"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />
        <Route
          path="/portal/farmmap"
          element={
            <PortalProtectedRoute>
              <Navigate to="/portal/farmhand" replace />
            </PortalProtectedRoute>
          }
        />
        {/* Backwards compatibility redirects for old /portal/planner routes */}
        <Route path="/portal/planner" element={<Navigate to="/portal/farmhand" replace />} />
        <Route path="/portal/planner/batches" element={<Navigate to="/portal/farmhand/batches" replace />} />
        <Route path="/portal/planner/weather" element={<Navigate to="/portal/farmhand/weather" replace />} />
        <Route path="/portal/planner/create" element={<Navigate to="/portal/farmhand/create" replace />} />
        <Route path="/portal/planner/plans/:planId/edit" element={<Navigate to="/portal/farmhand/plans/:planId/edit" replace />} />
        {/* Public route for order verification (no auth required) */}
        <Route path="/portal/verify-order/:token" element={<OrderVerification />} />

        {/* Mill Portal Routes */}
        {/* Mill uses shared team login - redirect to admin login */}
        <Route path="/mill/login" element={<Navigate to="/login?next=/mill/dashboard" replace />} />
        <Route
          path="/mill/dashboard"
          element={
            <MillProtectedRoute>
              <MillDashboard />
            </MillProtectedRoute>
          }
        />
        <Route
          path="/mill/mixing/:id"
          element={
            <MillProtectedRoute>
              <BatchMixing />
            </MillProtectedRoute>
          }
        />
        <Route
          path="/mill/mixing"
          element={
            <MillProtectedRoute>
              <MillMixing />
            </MillProtectedRoute>
          }
        />
        <Route
          path="/mill/packaging"
          element={
            <MillProtectedRoute>
              <MillPackaging />
            </MillProtectedRoute>
          }
        />
        <Route
          path="/mill/shipping"
          element={
            <MillProtectedRoute>
              <MillShipping />
            </MillProtectedRoute>
          }
        />
        <Route
          path="/mill/inventory"
          element={
            <MillProtectedRoute>
              <MillInventory />
            </MillProtectedRoute>
          }
        />
        <Route
          path="/mill/feed-bank"
          element={
            <MillProtectedRoute>
              <MillFeedBank />
            </MillProtectedRoute>
          }
        />
        <Route
          path="/mill/calendar"
          element={
            <MillProtectedRoute>
              <MillCalendar />
            </MillProtectedRoute>
          }
        />
        <Route
          path="/mill/orders"
          element={
            <MillProtectedRoute>
              <MillOrders />
            </MillProtectedRoute>
          }
        />
        <Route
          path="/mill/labels"
          element={
            <MillProtectedRoute>
              <LabelManagement />
            </MillProtectedRoute>
          }
        />
        <Route
          path="/mill/command"
          element={
            <MillProtectedRoute>
              <MillCommandDashboard />
            </MillProtectedRoute>
          }
        />
        <Route
          path="/mill/recall"
          element={
            <MillProtectedRoute>
              <RecallLookup />
            </MillProtectedRoute>
          }
        />
        <Route path="/mill" element={<Navigate to="/mill/dashboard" replace />} />

        {/* Store Portal Routes */}
        {/* Store uses shared team login - redirect to admin login */}
        <Route path="/store/login" element={<Navigate to="/login?next=/store/dashboard" replace />} />
        <Route
          path="/store/dashboard"
          element={
            <StoreProtectedRoute>
              <StoreDashboard />
            </StoreProtectedRoute>
          }
        />
        <Route
          path="/store/inventory"
          element={
            <StoreProtectedRoute>
              <StoreInventory />
            </StoreProtectedRoute>
          }
        />
        <Route
          path="/store/bins"
          element={
            <StoreProtectedRoute>
              <StoreBulkBins />
            </StoreProtectedRoute>
          }
        />
        <Route
          path="/store/scan"
          element={
            <StoreProtectedRoute>
              <StoreScan />
            </StoreProtectedRoute>
          }
        />
        <Route
          path="/store/orders/new"
          element={
            <StoreProtectedRoute>
              <StoreOrderEntry />
            </StoreProtectedRoute>
          }
        />
        {/* Legacy club-orders redirects to dashboard (now handled in POS) */}
        <Route path="/store/club-orders" element={<Navigate to="/store/dashboard" replace />} />
        <Route
          path="/store/checkin"
          element={
            <StoreProtectedRoute>
              <StoreCheckin />
            </StoreProtectedRoute>
          }
        />
        {/* Legacy bridge-club redirects to POS (member lookup is there) */}
        <Route path="/store/bridge-club" element={<Navigate to="/store/pos" replace />} />
        <Route
          path="/store/tasks"
          element={
            <StoreProtectedRoute>
              <StoreTasks />
            </StoreProtectedRoute>
          }
        />
        <Route
          path="/store/api-integration"
          element={
            <StoreProtectedRoute>
              <StoreAPIIntegration />
            </StoreProtectedRoute>
          }
        />
        {/* POS Checkout System */}
        <Route
          path="/store/pos"
          element={
            <StoreProtectedRoute>
              <StorePOS />
            </StoreProtectedRoute>
          }
        />
        <Route
          path="/store/pos/daily-report"
          element={
            <StoreProtectedRoute>
              <POSDailyReport />
            </StoreProtectedRoute>
          }
        />
        {/* Scan & Go Verification */}
        <Route
          path="/store/scan-go-verify"
          element={
            <StoreProtectedRoute>
              <ScanGoVerification />
            </StoreProtectedRoute>
          }
        />
        {/* Store Admin Dashboard */}
        <Route
          path="/store/admin"
          element={
            <StoreProtectedRoute>
              <StoreAdmin />
            </StoreProtectedRoute>
          }
        />
        {/* Legacy POS Products Admin - Redirect to Admin Portal */}
        <Route
          path="/store/admin/products"
          element={<Navigate to="/admin/pos/products" replace />}
        />
        {/* Barcode Scanner Tool */}
        <Route
          path="/store/barcode-scanner"
          element={
            <StoreProtectedRoute>
              <BarcodeScanner />
            </StoreProtectedRoute>
          }
        />
        {/* Unified Orders Page */}
        <Route
          path="/store/orders"
          element={
            <StoreProtectedRoute>
              <StoreOrders />
            </StoreProtectedRoute>
          }
        />
        <Route
          path="/store/orders/:orderNumber"
          element={
            <StoreProtectedRoute>
              <BridgeClubOrderDetail />
            </StoreProtectedRoute>
          }
        />
        {/* Locker Management Dashboard - full system with CAPTCHA, Eufy integration */}
        <Route
          path="/store/lockers"
          element={
            <StoreProtectedRoute>
              <LockerDashboard />
            </StoreProtectedRoute>
          }
        />
        {/* Pickup Orders */}
        <Route
          path="/store/pickup-orders"
          element={
            <StoreProtectedRoute>
              <PickupOrders />
            </StoreProtectedRoute>
          }
        />
        {/* Past Orders History — unified searchable list across all
            order types (POS, BC, Wholesale, StoreClub). Click-through
            to existing detail pages; Reopen affordance for recently-
            completed BC orders. */}
        <Route
          path="/store/orders-history"
          element={
            <StoreProtectedRoute>
              <OrdersHistory />
            </StoreProtectedRoute>
          }
        />
        {/* BYOC Bin Labels */}
        <Route
          path="/store/byoc-labels"
          element={
            <StoreProtectedRoute>
              <BYOCLabels />
            </StoreProtectedRoute>
          }
        />
        {/* Product QR Codes */}
        <Route
          path="/store/product-qr-codes"
          element={
            <StoreProtectedRoute>
              <ProductQRCodes />
            </StoreProtectedRoute>
          }
        />
        <Route path="/store" element={<Navigate to="/store/dashboard" replace />} />

        {/* Delivery Portal Routes */}
        <Route
          path="/delivery/dashboard"
          element={
            <DeliveryProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading…</div>
                  </div>
                }
              >
                <DeliveryDashboard />
              </React.Suspense>
            </DeliveryProtectedRoute>
          }
        />
        <Route
          path="/delivery/leads"
          element={
            <DeliveryProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading…</div>
                  </div>
                }
              >
                <DeliveryLeads />
              </React.Suspense>
            </DeliveryProtectedRoute>
          }
        />
        <Route
          path="/delivery/leads/map"
          element={
            <DeliveryProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading…</div>
                  </div>
                }
              >
                <DeliveryLeadsMap />
              </React.Suspense>
            </DeliveryProtectedRoute>
          }
        />
        <Route
          path="/delivery/leads/:id"
          element={
            <DeliveryProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading…</div>
                  </div>
                }
              >
                <DeliveryLeadDetail />
              </React.Suspense>
            </DeliveryProtectedRoute>
          }
        />
        <Route
          path="/delivery/pet-mapping"
          element={
            <DeliveryProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading…</div>
                  </div>
                }
              >
                <PetMapping />
              </React.Suspense>
            </DeliveryProtectedRoute>
          }
        />
        <Route
          path="/delivery/catalog"
          element={
            <DeliveryProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading…</div>
                  </div>
                }
              >
                <DeliveryCatalog />
              </React.Suspense>
            </DeliveryProtectedRoute>
          }
        />
        <Route
          path="/delivery/routes"
          element={
            <DeliveryProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading…</div>
                  </div>
                }
              >
                <DeliveryRoutes />
              </React.Suspense>
            </DeliveryProtectedRoute>
          }
        />
        <Route
          path="/delivery/activation"
          element={
            <DeliveryProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading…</div>
                  </div>
                }
              >
                <DeliveryActivation />
              </React.Suspense>
            </DeliveryProtectedRoute>
          }
        />
        <Route
          path="/delivery/landing"
          element={
            <DeliveryProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading…</div>
                  </div>
                }
              >
                <DeliveryLanding />
              </React.Suspense>
            </DeliveryProtectedRoute>
          }
        />
        <Route
          path="/delivery/orders"
          element={
            <DeliveryProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading…</div>
                  </div>
                }
              >
                <DeliveryOrders />
              </React.Suspense>
            </DeliveryProtectedRoute>
          }
        />
        <Route
          path="/delivery/procurement"
          element={
            <DeliveryProtectedRoute>
              <React.Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <div>Loading…</div>
                  </div>
                }
              >
                <DeliveryProcurement />
              </React.Suspense>
            </DeliveryProtectedRoute>
          }
        />
        {/* The Delivery Portal's DRIVER permission level: the route and the stop flow.
            DriverRoot gates it (offline start) - no DeliveryProtectedRoute here. */}
        <Route path="/delivery/driver/*" element={<DriverRoot />} />
        {/* Property walk: managers onboard a customer on their property. Same offline
            start as the driver view; the server gates every call to managers. */}
        <Route path="/delivery/walk/*" element={<WalkRoot />} />
        <Route path="/delivery" element={<DeliveryHome />} />

        {/* Vendor Portal (standalone, scoped external vendor login) */}
        <Route path="/vendor" element={<VendorPortal />} />

        {/* Store Club Portal Routes */}
        <Route path="/club" element={<LocalCheck />} />
        <Route path="/club/join" element={<LocalCheck />} />
        <Route path="/club/register" element={<OnboardingFlow />} />
        <Route path="/club/login" element={<ClubLogin />} />
        {/* Alternate paths for Store Club */}
        <Route path="/store-club" element={<Navigate to="/club" replace />} />
        <Route path="/store-club/*" element={<Navigate to="/club" replace />} />
        <Route
          path="/club/dashboard"
          element={
            <StoreClubProtectedRoute>
              <ClubDashboard />
            </StoreClubProtectedRoute>
          }
        />
        <Route
          path="/club/shop"
          element={
            <StoreClubProtectedRoute>
              <ClubShop />
            </StoreClubProtectedRoute>
          }
        />
        <Route
          path="/club/cart"
          element={
            <StoreClubProtectedRoute>
              <ClubCart />
            </StoreClubProtectedRoute>
          }
        />
        <Route
          path="/club/checkout"
          element={
            <StoreClubProtectedRoute>
              <ClubCheckout />
            </StoreClubProtectedRoute>
          }
        />
        <Route
          path="/club/orders"
          element={
            <StoreClubProtectedRoute>
              <ClubOrders />
            </StoreClubProtectedRoute>
          }
        />
        <Route
          path="/club/orders/:id"
          element={
            <StoreClubProtectedRoute>
              <ClubOrderDetail />
            </StoreClubProtectedRoute>
          }
        />
        <Route
          path="/club/farm"
          element={
            <StoreClubProtectedRoute>
              <ClubFarm />
            </StoreClubProtectedRoute>
          }
        />
        <Route
          path="/club/profile"
          element={
            <StoreClubProtectedRoute>
              <ClubProfile />
            </StoreClubProtectedRoute>
          }
        />
        <Route
          path="/club/card"
          element={
            <StoreClubProtectedRoute>
              <ClubCard />
            </StoreClubProtectedRoute>
          }
        />

        {/* Bridge Club Routes */}
        <Route path="/bridge-club" element={<BridgeClubOnboarding />} />
        <Route path="/bridge-club/terms" element={<BridgeClubTerms />} />
        <Route path="/bridge-club/privacy" element={<BridgeClubPrivacy />} />
        <Route path="/bridge-club/login" element={<BridgeClubLogin />} />
        <Route path="/bridge-club/reset-password" element={<BridgeClubResetPassword />} />
        <Route path="/bridge-club/verify-email/:token" element={<BridgeClubVerifyEmail />} />
        {/* Public: the single-use card link emailed after a property walk. */}
        <Route path="/bridge-club/card-link/:token" element={<BridgeClubCardLink />} />
        <Route
          path="/bridge-club/onboarding"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubSetupFlow />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/dashboard"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubDashboard />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/card"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubCard />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/delivery"
          element={
            <BridgeClubProtectedRoute>
              <DeliveryPage />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/animals"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubAnimals />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/feed"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubFeed />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/purchases"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubPurchases />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/upload"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubUploadReceipt />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/profile"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubProfile />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/find-store"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubStoreFinder />
            </BridgeClubProtectedRoute>
          }
        />
        {/* Resources routes */}
        <Route
          path="/bridge-club/resources"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubResources />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/resources/:slug"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubResources />
            </BridgeClubProtectedRoute>
          }
        />
        {/* Tools routes */}
        <Route
          path="/bridge-club/tools/predator-id"
          element={
            <BridgeClubProtectedRoute>
              <PredatorIdentifierPage />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/tools/health-assistant"
          element={
            <BridgeClubProtectedRoute>
              <FlockHealthAssistantPage />
            </BridgeClubProtectedRoute>
          }
        />
        {/* Farm Challenge routes */}
        <Route
          path="/bridge-club/farm-challenge"
          element={
            <BridgeClubProtectedRoute>
              <FarmChallengeHub />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/farm-challenge/join"
          element={
            <BridgeClubProtectedRoute>
              <FarmChallengeJoin />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/farm-challenge/opt-in"
          element={
            <BridgeClubProtectedRoute>
              <FarmChallengeOptIn />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/farm-challenge/log"
          element={
            <BridgeClubProtectedRoute>
              <FarmChallengeHub />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/farm-challenge/history"
          element={
            <BridgeClubProtectedRoute>
              <FarmChallengeHistory />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/farm-challenge/redeem"
          element={
            <BridgeClubProtectedRoute>
              <FarmChallengeRedeem />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/prayer-request"
          element={
            <BridgeClubProtectedRoute>
              <BridgeClubProfile />
            </BridgeClubProtectedRoute>
          }
        />

        {/* Local Access Shopping (Mill Members) */}
        <Route
          path="/bridge-club/shop"
          element={
            <BridgeClubProtectedRoute>
              <LocalShop />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/cart"
          element={
            <BridgeClubProtectedRoute>
              <LocalCart />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/local-orders"
          element={
            <BridgeClubProtectedRoute>
              <LocalOrders />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/local-orders/:id"
          element={
            <BridgeClubProtectedRoute>
              <LocalOrderDetail />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/byoc"
          element={
            <BridgeClubProtectedRoute>
              <LocalBYOC />
            </BridgeClubProtectedRoute>
          }
        />

        {/* Bulk Pre-Order (Custom Feed Orders) */}
        <Route
          path="/bridge-club/bulk-order"
          element={
            <BridgeClubProtectedRoute>
              <BulkPreOrder />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/bulk-orders"
          element={
            <BridgeClubProtectedRoute>
              <BulkPreOrder />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/bulk-orders/:orderNumber"
          element={
            <BridgeClubProtectedRoute>
              <BulkOrderDetail />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/tax-exemption"
          element={
            <BridgeClubProtectedRoute>
              <BCTaxExemption />
            </BridgeClubProtectedRoute>
          }
        />

        {/* Scan & Go Self-Checkout (Mill Members) */}
        <Route
          path="/bridge-club/scan-and-go"
          element={
            <BridgeClubProtectedRoute>
              <ScanAndGo />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/scan-and-go/checkout"
          element={
            <BridgeClubProtectedRoute>
              <ScanAndGoCheckout />
            </BridgeClubProtectedRoute>
          }
        />
        <Route
          path="/bridge-club/scan-and-go/receipt/:sessionId"
          element={
            <BridgeClubProtectedRoute>
              <ScanAndGoReceipt />
            </BridgeClubProtectedRoute>
          }
        />
        {/* Mill Store - Online Ordering */}
        <Route
          path="/bridge-club/mill-store"
          element={
            <BridgeClubProtectedRoute>
              <MillStore />
            </BridgeClubProtectedRoute>
          }
        />

        {/* Public Store Locator (no auth required) */}
        <Route path="/find-a-store" element={<StoreLocator />} />
        <Route path="/find-a-store/embed" element={<StoreLocatorEmbed />} />

        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      {/* PWA Install Prompt - only show on portal subdomains */}
      {installAppName && (
        <InstallPrompt appName={installAppName} themeColor={installThemeColor} />
      )}
    </>
  );
}

function App() {
  // Check for public routes FIRST, before any auth providers are mounted
  // This prevents all auth context useEffects from firing on public pages
  const pathname = window.location.pathname;
  const isPublicStoreLocator = pathname.startsWith('/find-a-store');
  const isBookingPage = pathname.startsWith('/book');

  // Render public routes completely outside all auth providers
  if (isPublicStoreLocator || isBookingPage) {
    return (
      <Router>
        <ScrollToTop />
        <Routes>
          <Route path="/find-a-store" element={<StoreLocator />} />
          <Route path="/find-a-store/embed" element={<StoreLocatorEmbed />} />
          <Route path="/book/cancel/:appointmentId" element={<BookingPage />} />
          <Route path="/book/portal-walkthrough" element={<BookingNDAGate />} />
          <Route path="/book/:slug" element={<BookingPage />} />
        </Routes>
      </Router>
    );
  }

  // Everything else gets wrapped in auth providers
  return (
    <Router>
      <ScrollToTop />
      <RefreshProvider>
        <DraftProvider>
        <AuthProvider>
          <PortalAuthProvider>
            <PortalLocationProvider>
              <TutorialProvider>
              <MillAuthProvider>
                <StoreAuthProvider>
                  <StoreClubAuthProvider>
                    <BridgeClubAuthProvider>
                      <AppContent />
                    </BridgeClubAuthProvider>
                  </StoreClubAuthProvider>
                </StoreAuthProvider>
              </MillAuthProvider>
              </TutorialProvider>
            </PortalLocationProvider>
          </PortalAuthProvider>
        </AuthProvider>
        </DraftProvider>
      </RefreshProvider>
    </Router>
  );
}

export default App;
